import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useAppContext } from "../../../context/AppContext";
import { ConnectionModel } from "../../../models/workFlow/ConnectionModel";
import {
  createConnection,
  testConnection,
  updateConnection,
} from "../../../services/AdminServices/ConnectionService";

interface ConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection?: ConnectionModel | null;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  connectionType: string;
  description: string;
  actionType: string;
  triggerType: string;
  // API / Azure Function fields
  apiUrl: string;
  apiKey: string;
  authType: string;
  apiKeyHeaderName: string;
  authUsername: string;
  authPassword: string;
  clientId: string;
  clientSecret: string;
  oAuthTokenUrl: string;
  // Database fields
  dbServer: string;
  dbName: string;
  dbUsername: string;
  dbPassword: string;
  dbPort: number;
  dbType: string;
  swaggerEnable: boolean;
  swaggerUrl: string;
}

interface FormErrors {
  [key: string]: string;
}

const TRIGGER_TYPES = [
  { value: "New Mail", label: "New Mail" },
  { value: "Reply To Mail", label: "Reply To Mail" },
  { value: "Forward Mail", label: "Forward Mail" },
  { value: "New and Reply", label: "New and Reply" },
  { value: "New and Forward", label: "New and Forward" },
];

/** Stored on the connection record; distinct from generic API connections. */
export const CONNECTION_TYPE_AZURE_FUNCTIONS = "azurefunction";

const ConnectionModal: React.FC<ConnectionModalProps> = ({
  isOpen,
  onClose,
  connection,
  onSuccess,
}) => {
  const { accessToken, userData } = useAppContext();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showDbPassword, setShowDbPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [testLoading, setTestLoading] = useState(false);

  const emptyForm: FormData = {
    name: "",
    connectionType: "",
    description: "",
    actionType: "",
    triggerType: "",
    apiUrl: "",
    apiKey: "",
    authType: "",
    apiKeyHeaderName: "",
    authUsername: "",
    authPassword: "",
    clientId: "",
    clientSecret: "",
    oAuthTokenUrl: "",
    dbServer: "",
    dbName: "",
    dbUsername: "",
    dbPassword: "",
    dbPort: 0,
    dbType: "",
    swaggerEnable: false,
    swaggerUrl: "",
  };

  const [formData, setFormData] = useState<FormData>(emptyForm);

  const isEditMode = Boolean(connection);
  const isAzureFunctions =
    formData.connectionType === CONNECTION_TYPE_AZURE_FUNCTIONS;

  useEffect(() => {
    if (isOpen) {
      if (connection) {
        const rawCt = (connection.connectionType || "").toLowerCase();
        const at = connection.actionType || "";
        const legacyUiActionAsAzure =
          rawCt === "api" &&
          at === "UI Action" &&
          !!(
            connection.triggerType ||
            (connection.apiKeyHeaderName || "").toLowerCase() ===
              "x-functions-key"
          );
        const displayConnectionType = legacyUiActionAsAzure
          ? CONNECTION_TYPE_AZURE_FUNCTIONS
          : connection.connectionType || "";

        setFormData({
          name: connection.name || "",
          connectionType: displayConnectionType,
          description: connection.description || "",
          actionType: connection.actionType || "",
          triggerType: connection.triggerType || "",
          apiUrl: connection.apiUrl || "",
          apiKey: connection.apiKey || "",
          authType: connection.authType || "",
          apiKeyHeaderName: connection.apiKeyHeaderName || "",
          authUsername: connection.authUsername || "",
          authPassword: connection.authPassword || "",
          clientId: connection.clientId || "",
          clientSecret: connection.clientSecret || "",
          oAuthTokenUrl: connection.oAuthTokenUrl || "",
          dbServer: connection.dbServer || "",
          dbName: connection.dbName || "",
          dbUsername: connection.dbUsername || "",
          dbPassword: connection.dbPassword || "",
          dbPort: connection.dbPort ? Number(connection.dbPort) : 0,
          dbType: connection.dbType || "",
          swaggerEnable: connection.isSwaggerEnabled,
          swaggerUrl: connection.swaggerUrl || "",
        });
      } else {
        setFormData(emptyForm);
      }
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "connectionType") {
        if (value === CONNECTION_TYPE_AZURE_FUNCTIONS) {
          next.dbServer = "";
          next.dbName = "";
          next.dbUsername = "";
          next.dbPassword = "";
          next.dbPort = 0;
          next.dbType = "";
        }
        if (value !== CONNECTION_TYPE_AZURE_FUNCTIONS) {
          next.triggerType = "";
          if (value === "database") {
            next.authType = "";
            next.apiUrl = "";
            next.apiKey = "";
          }
          if (value === "api") {
            next.authType = next.authType || "";
          }
        }
      }
      return next;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.connectionType) {
      newErrors.connectionType = "Connection type is required";
    }
    if (!formData.actionType) {
      newErrors.actionType = "Action type is required";
    }

    if (isAzureFunctions) {
      if (!formData.triggerType) {
        newErrors.triggerType = "Trigger type is required";
      }
      if (!formData.apiUrl.trim()) {
        newErrors.apiUrl = "Azure Function URL is required";
      } else if (!isValidUrl(formData.apiUrl)) {
        newErrors.apiUrl = "Please enter a valid URL";
      }
      if (!formData.apiKey.trim()) {
        newErrors.apiKey = "Function Code is required";
      }
      if (formData.swaggerEnable && !formData.swaggerUrl.trim()) {
        newErrors.swaggerUrl = "Swagger URL is required";
      } else if (formData.swaggerEnable && !isValidUrl(formData.swaggerUrl)) {
        newErrors.swaggerUrl = "Please enter a valid Swagger URL";
      }
    } else {
      if (formData.connectionType === "api") {
        if (!formData.apiUrl.trim()) {
          newErrors.apiUrl = "API URL is required";
        } else if (!isValidUrl(formData.apiUrl)) {
          newErrors.apiUrl = "Please enter a valid URL";
        }
        if (formData.authType === "apikey" && !formData.apiKey.trim()) {
          newErrors.apiKey = "API Key is required";
        }
        if (formData.authType === "bearer" && !formData.apiKey.trim()) {
          newErrors.apiKey = "Bearer Token is required";
        }
        if (formData.authType === "basic") {
          if (!formData.authUsername.trim()) newErrors.authUsername = "Username is required for Basic auth";
          if (!formData.authPassword.trim()) newErrors.authPassword = "Password is required for Basic auth";
        }
        if (formData.authType === "oauth" || formData.authType === "oauth2") {
          if (!formData.clientId.trim()) newErrors.clientId = "Client ID is required";
          if (!formData.clientSecret.trim()) newErrors.clientSecret = "Client Secret is required";
        }
        if (formData.authType === "custom") {
          if (!formData.apiKey.trim()) {
            newErrors.apiKey = "Custom header value is required (format: HeaderName:HeaderValue)";
          } else if (!formData.apiKey.includes(":")) {
            newErrors.apiKey = "Custom header must be in format 'HeaderName:HeaderValue'";
          }
        }
      }

      if (formData.connectionType === "database") {
        if (!formData.dbServer.trim()) newErrors.dbServer = "Database server is required";
        if (!formData.dbName.trim()) newErrors.dbName = "Database name is required";
        if (!formData.dbUsername.trim()) newErrors.dbUsername = "Database username is required";
        if (!formData.dbPassword.trim()) newErrors.dbPassword = "Database password is required";
        if (!formData.dbType) newErrors.dbType = "Database type is required";
        if (formData.dbPort <= 0) newErrors.dbPort = "Valid port number is required";
      }

      if (formData.swaggerEnable && !formData.swaggerUrl.trim()) {
        newErrors.swaggerUrl = "Swagger URL is required";
      } else if (formData.swaggerEnable && !isValidUrl(formData.swaggerUrl)) {
        newErrors.swaggerUrl = "Please enter a valid Swagger URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const buildConnectionData = (): ConnectionModel => ({
    connectionId: connection?.connectionId || 0,
    tenantId: userData!.tenantID,
    name: formData.name,
    connectionType: formData.connectionType,
    description: formData.description,
    actionType: formData.actionType,
    // JSON.stringify drops `undefined`; backend needs an explicit value to bind TriggerType.
    triggerType: isAzureFunctions
      ? formData.triggerType.trim()
      : "",
    apiUrl: formData.apiUrl,
    apiKey: formData.apiKey,
    authType: isAzureFunctions ? "apikey" : formData.authType,
    apiKeyHeaderName: isAzureFunctions
      ? "x-functions-key"
      : formData.apiKeyHeaderName,
    authUsername: formData.authUsername,
    authPassword: formData.authPassword,
    clientId: formData.clientId,
    clientSecret: formData.clientSecret,
    oAuthTokenUrl: formData.oAuthTokenUrl,
    dbServer: formData.dbServer,
    dbName: formData.dbName,
    dbUsername: formData.dbUsername,
    dbPassword: formData.dbPassword,
    dbPort: formData.dbPort > 0 ? String(formData.dbPort) : "",
    dbType: formData.dbType,
    createdBy: isEditMode ? connection?.createdBy || userData!.userID : userData!.userID,
    updatedBy: userData!.userID,
    createdAt: connection?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isDeleted: false,
    isSwaggerEnabled: formData.swaggerEnable,
    swaggerUrl: formData.swaggerEnable ? formData.swaggerUrl.trim() : "",
  });

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!accessToken || !userData?.tenantID) {
      toast({ title: "Error", description: "Authentication required", status: "error", duration: 3000, isClosable: true });
      return;
    }

    setLoading(true);
    try {
      const connectionData = buildConnectionData();
      const result = isEditMode
        ? await updateConnection(accessToken, connectionData)
        : await createConnection(accessToken, connectionData);

      if (result.success) {
        toast({
          title: "Success",
          description: result.message || `Connection ${isEditMode ? "updated" : "created"} successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: "Error",
          description: result.message || `Failed to ${isEditMode ? "update" : "create"} connection`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "An unexpected error occurred.", status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setErrors({});
    onClose();
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast({ title: "Validation Error", description: "Please fix the form errors before testing", status: "error", duration: 3000, isClosable: true });
      return;
    }
    if (!accessToken || !userData?.tenantID) {
      toast({ title: "Error", description: "Authentication required", status: "error", duration: 3000, isClosable: true });
      return;
    }

    setTestLoading(true);
    try {
      const data: ConnectionModel = {
        connectionId: 0,
        tenantId: userData.tenantID,
        createdBy: userData.userID,
        isDeleted: false,
        name: formData.name,
        connectionType: formData.connectionType,
        actionType: formData.actionType,
        triggerType: isAzureFunctions
          ? formData.triggerType.trim()
          : "",
        apiUrl: formData.apiUrl,
        apiKey: formData.apiKey,
        authType: isAzureFunctions ? "apikey" : formData.authType,
        apiKeyHeaderName: isAzureFunctions
          ? "x-functions-key"
          : formData.apiKeyHeaderName,
        authUsername: formData.authUsername,
        authPassword: formData.authPassword,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        oAuthTokenUrl: formData.oAuthTokenUrl,
        dbServer: formData.dbServer,
        dbName: formData.dbName,
        dbUsername: formData.dbUsername,
        dbPassword: formData.dbPassword,
        dbPort: formData.dbPort > 0 ? String(formData.dbPort) : "",
        dbType: formData.dbType,
        isSwaggerEnabled: formData.swaggerEnable,
        swaggerUrl: formData.swaggerEnable ? formData.swaggerUrl.trim() : "",
      };

      const result = await testConnection(accessToken, data);
      toast({
        title: result.success ? "Connection Test Successful" : "Connection Test Failed",
        description: result.message || (result.success ? "Connection test passed" : "Connection test failed"),
        status: result.success ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });
    } catch {
      toast({ title: "Test Connection Error", description: "An unexpected error occurred while testing", status: "error", duration: 5000, isClosable: true });
    } finally {
      setTestLoading(false);
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────────────────

  const renderBasicFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.name}>
        <FormLabel>Connection Name</FormLabel>
        <Input
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Enter connection name"
        />
        <FormErrorMessage>{errors.name}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.connectionType}>
        <FormLabel>Connection Type</FormLabel>
        <Select
          value={formData.connectionType}
          onChange={(e) => handleInputChange("connectionType", e.target.value)}
          placeholder="Select connection type"
        >
          <option value="api">API</option>
          <option value="database">Database</option>
          <option value={CONNECTION_TYPE_AZURE_FUNCTIONS}>
            Azure Functions
          </option>
        </Select>
        {formData.connectionType === CONNECTION_TYPE_AZURE_FUNCTIONS && (
          <FormHelperText>
            Outlook UI Actions: HTTP trigger URL and function key; set trigger
            and action type below. Optional Swagger/OpenAPI URL is configured in
            Azure Functions Configuration.
          </FormHelperText>
        )}
        {formData.connectionType === "api" && (
          <FormHelperText>
            General REST API: URL, authentication, and optional Swagger below.
          </FormHelperText>
        )}
        {formData.connectionType === "database" && (
          <FormHelperText>
            Database: server, port, credentials, and engine type below.
          </FormHelperText>
        )}
        <FormErrorMessage>{errors.connectionType}</FormErrorMessage>
      </FormControl>

      {formData.connectionType && (
        <FormControl isRequired isInvalid={!!errors.actionType}>
          <FormLabel>Action Type</FormLabel>
          <Select
            value={formData.actionType}
            onChange={(e) => handleInputChange("actionType", e.target.value)}
            placeholder="Select action type"
          >
            <option value="UI Action">UI Action</option>
            <option value="UI Flow">UI Flow</option>
          </Select>
          <FormHelperText>
            {isAzureFunctions
              ? "UI Action: taskpane / mapped mail context. UI Flow: Swagger-style multi-step flow where supported."
              : "How this connection is used in the admin and add-in."}
          </FormHelperText>
          <FormErrorMessage>{errors.actionType}</FormErrorMessage>
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Description</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Enter connection description"
          rows={3}
        />
      </FormControl>
    </VStack>
  );

  const renderUIActionFields = () => (
    <VStack spacing={4} align="stretch">
      {/* Trigger Type */}
      <FormControl isRequired isInvalid={!!errors.triggerType}>
        <FormLabel>Trigger Type</FormLabel>
        <Select
          value={formData.triggerType}
          onChange={(e) => handleInputChange("triggerType", e.target.value)}
          placeholder="Select trigger type"
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <FormHelperText>
          Defines which Outlook mail event triggers this action
        </FormHelperText>
        <FormErrorMessage>{errors.triggerType}</FormErrorMessage>
      </FormControl>

      <Divider />

      {/* Azure Function Configuration */}
      <Box>
        <HStack mb={3} spacing={2} align="center">
          <Text fontWeight="semibold" fontSize="md">
            Azure Function Configuration
          </Text>
          <Badge colorScheme="orange" variant="subtle">Azure</Badge>
        </HStack>

        <VStack spacing={4} align="stretch">
          <FormControl isRequired isInvalid={!!errors.apiUrl}>
            <FormLabel>Azure Function URL</FormLabel>
            <Input
              value={formData.apiUrl}
              onChange={(e) => handleInputChange("apiUrl", e.target.value)}
              placeholder="https://<app>.azurewebsites.net/api/<function>"
            />
            <FormHelperText>
              Full URL of your Azure Function HTTP trigger endpoint
            </FormHelperText>
            <FormErrorMessage>{errors.apiUrl}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.apiKey}>
            <FormLabel>Function Code (Auth Key)</FormLabel>
            <InputGroup>
              <Input
                type={showApiKey ? "text" : "password"}
                value={formData.apiKey}
                onChange={(e) => handleInputChange("apiKey", e.target.value)}
                placeholder="Enter Azure Function host or function key"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showApiKey ? "Hide key" : "Show key"}
                  icon={showApiKey ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowApiKey(!showApiKey)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormHelperText>
              Found in Azure Portal → Function App → App keys (host key) or
              Function → Function keys. Sent as <code>x-functions-key</code> header.
            </FormHelperText>
            <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
          </FormControl>
        </VStack>
      </Box>

      <Divider />

      <FormControl>
        <FormLabel>Swagger/OpenAPI</FormLabel>
        <Checkbox
          isChecked={formData.swaggerEnable}
          onChange={(e) => handleInputChange("swaggerEnable", e.target.checked)}
        >
          Enable Swagger Documentation
        </Checkbox>
      </FormControl>

      {formData.swaggerEnable && (
        <FormControl isRequired isInvalid={!!errors.swaggerUrl}>
          <FormLabel>Swagger URL</FormLabel>
          <Input
            value={formData.swaggerUrl}
            onChange={(e) => handleInputChange("swaggerUrl", e.target.value)}
            placeholder="https://api.example.azurewebsites.net/api/swagger.json"
          />
          <FormHelperText>URL to Swagger/OpenAPI documentation (JSON format)</FormHelperText>
          <FormErrorMessage>{errors.swaggerUrl}</FormErrorMessage>
        </FormControl>
      )}
    </VStack>
  );

  const renderApiFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.apiUrl}>
        <FormLabel>API URL</FormLabel>
        <Input
          value={formData.apiUrl}
          onChange={(e) => handleInputChange("apiUrl", e.target.value)}
          placeholder="https://api.example.com"
        />
        <FormErrorMessage>{errors.apiUrl}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.authType}>
        <FormLabel>Authentication Type</FormLabel>
        <Select
          value={formData.authType}
          onChange={(e) => handleInputChange("authType", e.target.value)}
          placeholder="Select authentication type"
        >
          <option value="none">None</option>
          <option value="bearer">Bearer Token</option>
          <option value="apikey">API Key</option>
          <option value="basic">Basic Authentication</option>
          <option value="oauth">OAuth</option>
          <option value="oauth2">OAuth 2.0</option>
          <option value="custom">Custom Header</option>
        </Select>
        <FormErrorMessage>{errors.authType}</FormErrorMessage>
      </FormControl>

      {formData.authType === "bearer" && (
        <FormControl isRequired isInvalid={!!errors.apiKey}>
          <FormLabel>Bearer Token</FormLabel>
          <InputGroup>
            <Input
              type={showApiKey ? "text" : "password"}
              value={formData.apiKey}
              onChange={(e) => handleInputChange("apiKey", e.target.value)}
              placeholder="Enter bearer token"
            />
            <InputRightElement>
              <IconButton
                aria-label={showApiKey ? "Hide token" : "Show token"}
                icon={showApiKey ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowApiKey(!showApiKey)}
                variant="ghost"
                size="sm"
              />
            </InputRightElement>
          </InputGroup>
          <FormHelperText>JWT or access token for authentication</FormHelperText>
          <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
        </FormControl>
      )}

      {formData.authType === "apikey" && (
        <>
          <FormControl isRequired isInvalid={!!errors.apiKey}>
            <FormLabel>API Key</FormLabel>
            <InputGroup>
              <Input
                type={showApiKey ? "text" : "password"}
                value={formData.apiKey}
                onChange={(e) => handleInputChange("apiKey", e.target.value)}
                placeholder="Enter API key"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showApiKey ? "Hide API key" : "Show API key"}
                  icon={showApiKey ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowApiKey(!showApiKey)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.apiKeyHeaderName}>
            <FormLabel>API Key Header Name (Optional)</FormLabel>
            <Input
              value={formData.apiKeyHeaderName}
              onChange={(e) => handleInputChange("apiKeyHeaderName", e.target.value)}
              placeholder="X-API-Key (default)"
            />
            <FormHelperText>
              Leave empty to use default "X-API-Key". Common: Authorization, X-API-Token, api-key
            </FormHelperText>
            <FormErrorMessage>{errors.apiKeyHeaderName}</FormErrorMessage>
          </FormControl>
        </>
      )}

      {formData.authType === "basic" && (
        <>
          <FormControl isRequired isInvalid={!!errors.authUsername}>
            <FormLabel>Username</FormLabel>
            <Input
              value={formData.authUsername}
              onChange={(e) => handleInputChange("authUsername", e.target.value)}
              placeholder="Enter username"
            />
            <FormErrorMessage>{errors.authUsername}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors.authPassword}>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <Input
                type={showAuthPassword ? "text" : "password"}
                value={formData.authPassword}
                onChange={(e) => handleInputChange("authPassword", e.target.value)}
                placeholder="Enter password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showAuthPassword ? "Hide password" : "Show password"}
                  icon={showAuthPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowAuthPassword(!showAuthPassword)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.authPassword}</FormErrorMessage>
          </FormControl>
        </>
      )}

      {(formData.authType === "oauth" || formData.authType === "oauth2") && (
        <>
          <FormControl isRequired isInvalid={!!errors.oAuthTokenUrl}>
            <FormLabel>Token URL</FormLabel>
            <Input
              value={formData.oAuthTokenUrl}
              onChange={(e) => handleInputChange("oAuthTokenUrl", e.target.value)}
              placeholder="Enter oAuthTokenUrl"
            />
            <FormErrorMessage>{errors.oAuthTokenUrl}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors.clientId}>
            <FormLabel>Client ID</FormLabel>
            <Input
              value={formData.clientId}
              onChange={(e) => handleInputChange("clientId", e.target.value)}
              placeholder="Enter client ID"
            />
            <FormErrorMessage>{errors.clientId}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors.clientSecret}>
            <FormLabel>Client Secret</FormLabel>
            <InputGroup>
              <Input
                type={showClientSecret ? "text" : "password"}
                value={formData.clientSecret}
                onChange={(e) => handleInputChange("clientSecret", e.target.value)}
                placeholder="Enter client secret"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showClientSecret ? "Hide client secret" : "Show client secret"}
                  icon={showClientSecret ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormHelperText>OAuth 2.0 Client Credentials Flow</FormHelperText>
            <FormErrorMessage>{errors.clientSecret}</FormErrorMessage>
          </FormControl>
        </>
      )}

      {formData.authType === "custom" && (
        <FormControl isRequired isInvalid={!!errors.apiKey}>
          <FormLabel>Custom Header</FormLabel>
          <InputGroup>
            <Input
              type={showApiKey ? "text" : "password"}
              value={formData.apiKey}
              onChange={(e) => handleInputChange("apiKey", e.target.value)}
              placeholder="HeaderName:HeaderValue"
            />
            <InputRightElement>
              <IconButton
                aria-label={showApiKey ? "Hide value" : "Show value"}
                icon={showApiKey ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowApiKey(!showApiKey)}
                variant="ghost"
                size="sm"
              />
            </InputRightElement>
          </InputGroup>
          <FormHelperText>
            Format: HeaderName:HeaderValue (e.g., "X-Custom-Auth:mysecretkey")
          </FormHelperText>
          <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
        </FormControl>
      )}

      <Divider />
      <FormControl>
        <FormLabel>Swagger/OpenAPI</FormLabel>
        <Checkbox
          isChecked={formData.swaggerEnable}
          onChange={(e) => handleInputChange("swaggerEnable", e.target.checked)}
        >
          Enable Swagger Documentation
        </Checkbox>
      </FormControl>

      {formData.swaggerEnable && (
        <FormControl isRequired isInvalid={!!errors.swaggerUrl}>
          <FormLabel>Swagger URL</FormLabel>
          <Input
            value={formData.swaggerUrl}
            onChange={(e) => handleInputChange("swaggerUrl", e.target.value)}
            placeholder="https://api.example.com/swagger/v1/swagger.json"
          />
          <FormHelperText>URL to Swagger/OpenAPI documentation (JSON format)</FormHelperText>
          <FormErrorMessage>{errors.swaggerUrl}</FormErrorMessage>
        </FormControl>
      )}
    </VStack>
  );

  const renderDatabaseFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.dbType}>
        <FormLabel>Database Type</FormLabel>
        <Select
          value={formData.dbType}
          onChange={(e) => handleInputChange("dbType", e.target.value)}
          placeholder="Select database type"
        >
          <option value="mysql">MySQL</option>
          <option value="postgresql">PostgreSQL</option>
          <option value="sqlserver">SQL Server</option>
          <option value="oracle">Oracle</option>
          <option value="mongodb">MongoDB</option>
        </Select>
        <FormErrorMessage>{errors.dbType}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbServer}>
        <FormLabel>Database Server</FormLabel>
        <Input
          value={formData.dbServer}
          onChange={(e) => handleInputChange("dbServer", e.target.value)}
          placeholder="localhost or server IP"
        />
        <FormErrorMessage>{errors.dbServer}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbPort}>
        <FormLabel>Port</FormLabel>
        <NumberInput
          value={formData.dbPort}
          onChange={(_, value) => handleInputChange("dbPort", value || 0)}
          min={1}
          max={65535}
        >
          <NumberInputField placeholder="3306" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <FormErrorMessage>{errors.dbPort}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbName}>
        <FormLabel>Database Name</FormLabel>
        <Input
          value={formData.dbName}
          onChange={(e) => handleInputChange("dbName", e.target.value)}
          placeholder="Enter database name"
        />
        <FormErrorMessage>{errors.dbName}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbUsername}>
        <FormLabel>Username</FormLabel>
        <Input
          value={formData.dbUsername}
          onChange={(e) => handleInputChange("dbUsername", e.target.value)}
          placeholder="Enter database username"
        />
        <FormErrorMessage>{errors.dbUsername}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbPassword}>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            type={showDbPassword ? "text" : "password"}
            value={formData.dbPassword}
            onChange={(e) => handleInputChange("dbPassword", e.target.value)}
            placeholder="Enter database password"
          />
          <InputRightElement>
            <IconButton
              aria-label={showDbPassword ? "Hide password" : "Show password"}
              icon={showDbPassword ? <ViewOffIcon /> : <ViewIcon />}
              onClick={() => setShowDbPassword(!showDbPassword)}
              variant="ghost"
              size="sm"
            />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{errors.dbPassword}</FormErrorMessage>
      </FormControl>
    </VStack>
  );

  const canSubmit =
    !!formData.name &&
    !!formData.connectionType &&
    !!formData.actionType &&
    (isAzureFunctions
      ? !!formData.triggerType &&
        !!formData.apiUrl &&
        !!formData.apiKey &&
        (!formData.swaggerEnable ||
          (!!formData.swaggerUrl.trim() && isValidUrl(formData.swaggerUrl)))
      : true);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="620px">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text fontSize="xl" fontWeight="bold">
              {isEditMode ? "Edit Connection" : "Create New Connection"}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {isEditMode ? "Update connection details" : "Configure your new data connection"}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Basic fields */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4}>
                Basic Information
              </Text>
              {renderBasicFields()}
            </Box>

            {/* Azure Functions (Outlook UI Actions) */}
            {isAzureFunctions && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Azure Functions Configuration
                  </Text>
                  {renderUIActionFields()}
                </Box>
              </>
            )}

            {/* Standard API / DB section */}
            {!isAzureFunctions && formData.connectionType && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    {formData.connectionType === "api" && "API Configuration"}
                    {formData.connectionType === "database" && "Database Configuration"}
                  </Text>
                  {formData.connectionType === "api" && renderApiFields()}
                  {formData.connectionType === "database" && renderDatabaseFields()}
                  {formData.connectionType === "file" && (
                    <Alert status="info">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>File Connection</AlertTitle>
                        <AlertDescription>
                          File connection configuration will be available in the next version.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            {!isAzureFunctions && (
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={loading}
                isLoading={testLoading}
                loadingText="Testing..."
              >
                Test Connection
              </Button>
            )}
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              disabled={!canSubmit}
            >
              {isEditMode ? "Update Connection" : "Create Connection"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConnectionModal;
